import React from 'react';

import { ia, search } from 'lib/helpers/utility';
import { useBillingRenderingProviders } from 'lib/hooks/queries/billing/useBillingRenderingProviders';
import { useFacilities } from 'lib/hooks/queries/facility/useFacilities';

import Select from 'components/shared/Select/Select';

import { getBillingAddress, getFacilityAddress } from '../lib/generateAddresses';
import { providerOptions } from '../lib/providerOptions';

export const FacilityInformation = ({ formik, superbill, states }) => {
  const { values: claim, setFieldValue } = formik;

  const { data: facilities } = useFacilities({
    options: {
      enabled: !!superbill.id
    }
  });

  const { data: billingRenderingProviders } = useBillingRenderingProviders({
    options: {
      enabled: !!superbill.id
    }
  });

  const facilityProviders = providerOptions(facilities?.facilityInfo, 'facility_name');
  const renderingProviders = providerOptions(
    billingRenderingProviders?.providers?.rendering,
    'fullName'
  );
  const billingProviders = providerOptions(
    billingRenderingProviders?.providers?.billing,
    'bill_name'
  );

  const currentFacilityProvider = facilityProviders.find(
    (provider) => provider?.facility_npi == claim?.facility_npi
  );
  const currentRenderingProvider = renderingProviders.find(
    (provider) =>
      provider?.billingInfo?.npi == claim?.prov_npi ||
      provider?.billingInfo?.tax_id == claim?.prov_taxid
  );
  const currentBillingProvider = billingProviders.find(
    (provider) => provider?.bill_npi == claim?.bill_npi || provider?.bill_taxid == claim?.bill_taxid
  );

  const selectRenderingProvider = (option) => {
    const fieldsToUpdate = {
      prov_name_l: option.l_name,
      prov_name_f: option.f_name,
      prov_name_m: option.m_name,
      prov_npi: option.billingInfo.npi,
      prov_id: option.billingInfo.atypical_id,
      prov_taxonomy: option.billingInfo.taxonomy,
      prov_taxid: option.billingInfo.tax_id,
      prov_taxid_type: option.billingInfo.tax_id_type
    };

    Object.entries(fieldsToUpdate).forEach(([fieldName, value]) => {
      setFieldValue(fieldName, value);
    });
  };

  const selectFacilityProvider = (option) => {
    const fieldsToUpdate = {
      facility_name: option.facility_name,
      facility_addr_1: option.facility_addr_1,
      facility_addr_2: option.facility_addr_2,
      facility_city: option.facility_city,
      facility_state: search(states, option.facility_state, 'id'),
      facility_zip: option.facility_zip,
      facility_npi: option.facility_npi,
      facility_id: option.facility_id
    };

    Object.entries(fieldsToUpdate).forEach(([fieldName, value]) => {
      setFieldValue(fieldName, value);
    });
  };

  const selectBillingProvider = (option) => {
    const fieldsToUpdate = {
      bill_name: option.bill_name,
      bill_addr_1: option.bill_addr_1,
      bill_addr_2: option.bill_addr_2,
      bill_city: option.bill_city,
      bill_state: search(states, option.bill_state, 'id'),
      bill_zip: option.bill_zip,
      bill_npi: option.bill_npi,
      bill_id: option.bill_id,
      bill_phone: option.bill_phone,
      bill_taxid: option.bill_taxid,
      bill_taxid_type: option.bill_taxid_type,
      bill_taxonomy: option.bill_taxonomy
    };

    Object.entries(fieldsToUpdate).forEach(([fieldName, value]) => {
      setFieldValue(fieldName, value);
    });
  };

  return (
    <div className="block rounded-md bg-white p-[1.25rem] shadow">
      <div className="flex flex-col items-start justify-between gap-8 lg:flex-row xl:flex-row">
        <div className="flex w-full flex-1 flex-col gap-1 lg:w-[400px]">
          <label className="block text-xl font-500">Facility information</label>
          {ia(facilityProviders) && (
            <Select
              className="print:hidden"
              options={facilityProviders}
              onChange={(e) => selectFacilityProvider(e)}
              value={currentFacilityProvider}
              isClearable={false}
            />
          )}
          <div className="flex gap-1">
            <label className="font-500">Name:</label>
            <div>{claim?.facility_name}</div>
          </div>
          <div className="flex gap-1">
            <label className="font-500">NPI: </label>
            <div>{claim?.facility_npi}</div>
          </div>
          <div className="flex gap-1">
            <label className="font-500">Atypical ID: </label>
            <div>{claim?.facility_id}</div>
          </div>
          <div className="flex gap-1">
            <label className="font-500">Address:</label>

            <div>{getFacilityAddress(claim)}</div>
          </div>
        </div>
        <div className="flex w-full flex-1 flex-col gap-1 lg:w-[400px]">
          <label className="block text-lg font-500">Rendering provider</label>
          {ia(renderingProviders) && (
            <Select
              className="print:hidden"
              options={renderingProviders}
              onChange={(e) => selectRenderingProvider(e)}
              value={currentRenderingProvider}
              isClearable={false}
            />
          )}
          <div className="flex gap-1">
            <label className="font-500">Name:</label>
            <div>{`${claim?.prov_name_f} ${claim?.prov_name_m} ${claim?.prov_name_l}`}</div>
          </div>
          <div className="flex gap-1">
            <label className="font-500">NPI: </label>
            <div>{claim?.prov_npi}</div>
          </div>
          <div className="flex gap-1">
            <label className="font-500">Tax ID: </label>
            <div>{claim?.prov_taxid}</div>
          </div>
          <div className="flex gap-1">
            <label className="font-500">Atypical ID: </label>
            <div>{claim?.prov_id}</div>
          </div>
          <div className="flex gap-1">
            <label className="font-500">Taxonomy: </label>
            <div>{claim?.prov_taxonomy}</div>
          </div>
        </div>
        <div className="flex w-full flex-1 flex-col gap-1 lg:w-[400px]">
          <label className="block text-lg font-500">Billing provider</label>
          {ia(billingProviders) && (
            <Select
              className="print:hidden"
              options={billingProviders}
              onChange={(e) => selectBillingProvider(e)}
              value={currentBillingProvider}
              isClearable={false}
            />
          )}
          <div className="flex gap-1">
            <label className="font-500">Name:</label>
            <div>{claim?.bill_name}</div>
          </div>
          <div className="flex gap-1">
            <label className="font-500">NPI: </label>
            <div>{claim?.bill_npi}</div>
          </div>
          <div className="flex gap-1">
            <label className="font-500">Tax ID: </label>
            <div>{claim?.bill_taxid}</div>
          </div>
          <div className="flex gap-1">
            <label className="font-500">Atypical ID: </label>
            <div>{claim?.bill_id}</div>
          </div>
          <div className="flex gap-1">
            <label className="font-500">Taxonomy: </label>
            <div>{claim?.bill_taxonomy}</div>
          </div>
          <div className="flex gap-1">
            <label className="font-500">Address:</label>
            <div>{getBillingAddress(claim)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};
